import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { crafExecutionService } from "../../services/crafExecutionService";
import { buildFolderPath } from "../../services/contractService";
import { PdfSigningViewer } from "./PdfSigningViewer";

// Not everything that gets thrown is a proper Error (a rejected fetch, a raw
// string, a plain object) — fall back so the approver always sees something
// actionable instead of a bare "undefined".
const describeError = (error) => {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;
  if (error.message) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

// CRAF approver signing page — mirrors ApproverSigningPage exactly (same PDF
// viewer, same signature/text-box flow, same submit UX) but reads/writes the
// CRAF-specific execution lists so CRAF approvals never touch the main
// contract's execution/approver records or its Status field.
export const CrafApproverSigningPage = () => {
  const navigate = useNavigate();
  const { service, currentUser } = useApp();
  const { executionId } = useParams();

  const [execution, setExecution] = useState(null);
  const [approver, setApprover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [mainContractDocLink, setMainContractDocLink] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [preAssignedSignature, setPreAssignedSignature] = useState(null);
  const [showSignatureUploadModal, setShowSignatureUploadModal] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadExecutionData();
    }
  }, [executionId, currentUser]);

  useEffect(() => {
    if (approver?.ApproverEmail) {
      loadPreAssignedSignature();
    }
  }, [approver?.ApproverEmail]);

  const loadExecutionData = async () => {
    setLoading(true);
    try {
      const exec = await crafExecutionService.getCrafExecution(service, executionId);
      if (!exec) {
        setError("CRAF execution not found");
        setLoading(false);
        return;
      }

      setExecution(exec);

      const allApprovers = await crafExecutionService.getCrafExecutionApprovers(service, executionId);

      if (!allApprovers || allApprovers.length === 0) {
        setError("No CRAF approvers found for this execution");
        setLoading(false);
        return;
      }

      const currentUserEmail = (currentUser?.mail || currentUser?.userPrincipalName || "").toLowerCase();
      const currentUserApprover = allApprovers.find(a =>
        a.ApproverEmail && currentUserEmail &&
        a.ApproverEmail.toLowerCase() === currentUserEmail
      );

      if (!currentUserApprover) {
        setError("You are not a CRAF approver for this contract");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Approved") {
        setError("You have already signed this CRAF document. No further action is needed.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Rejected") {
        setError("You have rejected this CRAF document. Access is no longer available.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Skipped") {
        setError("This CRAF approval was skipped for you. Access is no longer available.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Waiting") {
        setError("It is not your turn to approve the CRAF yet. Please wait for your turn.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status !== "Pending") {
        setError("You do not have permission to access this CRAF document at this time");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setApprover(currentUserApprover);

      // Load the CRAF PDF from SharePoint
      try {
        console.log('[CrafApproverSigningPage] Loading CRAF PDF for execution:', executionId);

        const contract = await service.getListItem("Contracts", exec.ContractID);
        const contractData = contract.fields;

        const drives = await service.getDrives();
        const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");

        if (!docsDrive) {
          console.warn('[CrafApproverSigningPage] ContractDocuments drive not found');
          return;
        }

        const driveId = docsDrive.id;

        const folderPath = buildFolderPath(
          contractData.Entity,
          contractData.Department,
          contractData.Year,
          contractData.Month,
          contractData.RefNo
        );

        const pdfFileName = crafExecutionService.getCrafPdfName(exec.ContractRefNo);
        const fullPath = `${folderPath}/${pdfFileName}.pdf`;

        console.log('[CrafApproverSigningPage] Looking for CRAF PDF at:', fullPath);

        try {
          const folderContents = await service.getFolderContents(driveId, folderPath);

          // Main contract's Word draft — linked here (not the executed PDF,
          // since that doesn't exist yet at CRAF-signing time; the contract
          // is typically only executed after the CRAF is). Identified as the
          // .docx that isn't the CRAF's own draft.
          const mainDraftFile = folderContents?.value?.find(item =>
            !item.folder && item.name && item.name.toLowerCase().endsWith(".docx") &&
            !item.name.toLowerCase().startsWith("craf-")
          );
          if (mainDraftFile?.webUrl) {
            setMainContractDocLink(mainDraftFile.webUrl);
          }

          const pdfFile = folderContents?.value?.find(item =>
            item.name && item.name.includes(pdfFileName) && !item.folder
          );

          if (pdfFile) {
            console.log('[CrafApproverSigningPage] ✅ Found CRAF PDF:', pdfFile.name, 'ID:', pdfFile.id);

            try {
              const pdfArrayBuffer = await service.downloadFile(driveId, fullPath);

              if (!pdfArrayBuffer || pdfArrayBuffer.byteLength === 0) {
                console.error('[CrafApproverSigningPage] CRAF PDF data is empty!');
                return;
              }

              const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
              const pdfDataUrlValue = URL.createObjectURL(pdfBlob);

              setPdfDataUrl(pdfDataUrlValue);
              console.log('[CrafApproverSigningPage] ✅ CRAF PDF state updated');
            } catch (downloadError) {
              console.error('[CrafApproverSigningPage] Failed to download CRAF PDF:', downloadError.message);
            }
          } else {
            console.warn('[CrafApproverSigningPage] CRAF PDF file not found in folder');
          }
        } catch (folderError) {
          console.warn('[CrafApproverSigningPage] Could not access folder:', folderError);
        }
      } catch (docError) {
        console.error('[CrafApproverSigningPage] Error loading contract/CRAF PDF:', docError);
      }
    } catch (e) {
      console.error("Error loading CRAF execution:", e);
      setError("Failed to load CRAF execution details");
    } finally {
      setLoading(false);
    }
  };

  const loadPreAssignedSignature = async () => {
    try {
      const signatures = await service.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approver.ApproverEmail}'`
      );

      if (signatures?.value?.length > 0) {
        // Duplicates may exist from before the save-signature fix — use the
        // most recently saved one rather than whatever order the API returns.
        const latest = signatures.value
          .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];
        const signature = latest.fields;
        setPreAssignedSignature(signature.SignatureData);
        console.log('[CrafApproverSigningPage] ✓ Loaded pre-assigned signature for', approver.ApproverEmail);
      } else {
        console.log('[CrafApproverSigningPage] ℹ️ No pre-assigned signature for', approver.ApproverEmail);
        setPreAssignedSignature(null);
        setShowSignatureUploadModal(true);
      }
    } catch (e) {
      console.error('[CrafApproverSigningPage] Error loading pre-assigned signature:', e);
    }
  };

  const handleSaveUserSignature = async (signatureBase64) => {
    if (!signatureBase64 || !approver?.ApproverEmail) return;

    setUploadingSignature(true);
    try {
      // One signature per email, ever — update if one already exists
      // instead of duplicating.
      const existing = await service.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approver.ApproverEmail}'`
      );
      // Duplicates may already exist from before this fix — pick the most
      // recent one rather than whatever order the API happens to return.
      const existingRecord = (existing?.value || [])
        .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];
      const fields = {
        Title: `${approver.ApproverEmail} - ${new Date().toLocaleDateString()}`,
        ApproverEmail: approver.ApproverEmail,
        SignatureData: signatureBase64,
        CreatedAt: new Date().toISOString()
      };

      let signature;
      if (existingRecord) {
        await service.updateListItem("ApproverSignatures", existingRecord.id, fields);
        signature = { id: existingRecord.id, ...fields };
      } else {
        signature = await service.createListItem("ApproverSignatures", fields);
      }

      console.log('[CrafApproverSigningPage] ✅ User signature saved:', signature.id);

      setPreAssignedSignature(signatureBase64);
      setShowSignatureUploadModal(false);
    } catch (error) {
      console.error('[CrafApproverSigningPage] Error saving signature:', error);
      alert("Error saving signature: " + describeError(error));
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleSignatureCaptured = async (sigData) => {
    console.log('[CrafApproverSigningPage] 🖊️ Signature captured:', {
      hasData: !!sigData.signatureData,
      page: sigData.signaturePage,
      textBoxCount: sigData.textBoxes?.length || 0,
      position: sigData.signaturePosition
    });

    await handleApproveWithPositions(sigData);
  };

  const handleApproveWithPositions = async (sigData) => {
    if (!sigData?.signatureData) {
      alert("Please position your signature first");
      return;
    }

    setSubmitting(true);
    try {
      // One signature per email, ever — update the existing record instead
      // of creating a new one on every single approval.
      const existingSig = await service.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approver.ApproverEmail}'`
      );
      // Duplicates may already exist from before this fix — pick the most
      // recent one rather than whatever order the API happens to return.
      const existingSigRecord = (existingSig?.value || [])
        .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];
      const signatureFields = {
        Title: `${approver.ApproverEmail} - ${new Date().toLocaleDateString()}`,
        ApproverEmail: approver.ApproverEmail,
        SignatureData: sigData.signatureData,
        CreatedAt: new Date().toISOString()
      };

      let signatureRecord;
      if (existingSigRecord) {
        await service.updateListItem("ApproverSignatures", existingSigRecord.id, signatureFields);
        signatureRecord = { id: existingSigRecord.id, ...signatureFields };
      } else {
        signatureRecord = await service.createListItem("ApproverSignatures", signatureFields);
      }

      const contractData = await service.getListItem("Contracts", execution.ContractID);
      const contract = contractData.fields;

      // contract.DriveId is only ever set by the main contract's execution
      // flow — CRAF is commonly run independently (often before the main
      // contract), so it can't be relied on here. Resolve the drive directly
      // instead, same as handleInitiateCrafExecution does when sending the CRAF.
      let driveId = contract.DriveId;
      if (!driveId) {
        const drives = await service.getDrives();
        const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");
        driveId = docsDrive?.id;
      }

      const contractFolder = buildFolderPath(
        contract.Entity,
        contract.Department,
        contract.Year,
        contract.Month,
        contract.RefNo
      );

      await crafExecutionService.approveCrafExecution(service, {
        approverId: approver.id,
        executionId: executionId,
        approverEmail: approver.ApproverEmail,
        signatureId: signatureRecord.id,
        comments: "",
        signatureData: sigData.signatureData,
        signaturePage: sigData.signaturePage,
        signatureX: sigData.signaturePosition?.x,
        signatureY: sigData.signaturePosition?.y,
        signatureWidth: sigData.signaturePosition?.width,
        signatureHeight: sigData.signaturePosition?.height,
        approverName: approver.ApproverName,
        textBoxes: sigData.textBoxes || [],
        driveId: driveId,
        contractFolder: contractFolder
      });

      const updatedExecution = await crafExecutionService.getCrafExecution(service, executionId);
      const approvers = await crafExecutionService.getCrafExecutionApprovers(service, executionId);
      const allApproved = approvers.every((a) => a.Status === "Approved");

      if (allApproved && updatedExecution.Status === "Completed") {
        alert("All CRAF approvers have signed! The CRAF execution is now complete. Thank you!");
      } else {
        alert("Your CRAF signature has been recorded. Thank you!");
      }

      navigate("/");
    } catch (error) {
      console.error("Error approving CRAF:", error);
      alert("Error recording CRAF approval: " + describeError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    setRejecting(true);
    try {
      await crafExecutionService.rejectCrafExecution(service, {
        approverId: approver.id,
        reason: rejectReason,
      });

      alert("CRAF document has been rejected. The admin has been notified.");
      navigate("/");
    } catch (error) {
      console.error("Error rejecting CRAF:", error);
      alert("Error rejecting: " + describeError(error));
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-yellow-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading CRAF document...</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
        <div className="bg-slate-800 border border-red-700 rounded-xl p-8 max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-red-600 font-serif text-2xl mb-3">Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col">
      {/* View Contract Document — links straight to the main contract's Word
          draft (not the executed PDF, which doesn't exist yet at this point in
          the flow). Opens in a new tab, does not disrupt the signing flow. */}
      {mainContractDocLink && (
        <a
          href={mainContractDocLink}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-800/95 hover:bg-slate-700 border border-slate-600 rounded-full text-xs font-medium text-yellow-500 shadow-lg backdrop-blur transition-colors"
        >
          📄 View Contract Document ↗
        </a>
      )}

      {/* Fullscreen PDF Viewer */}
      {pdfDataUrl ? (
        <PdfSigningViewer
          pdfDataUrl={pdfDataUrl}
          onSignatureCaptured={handleSignatureCaptured}
          contractTitle={execution?.ContractTitle ? `CRAF — ${execution.ContractTitle}` : "CRAF"}
          contractRef={execution?.ContractRefNo}
          approverEmail={approver?.ApproverEmail}
          approverName={approver?.ApproverName}
          approvalOrder={approver?.ApprovalOrder}
          totalApprovers={execution?.TotalApprovers}
          preAssignedSignature={preAssignedSignature}
          isSubmitting={submitting}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-slate-400 text-center">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-xl">Loading CRAF PDF...</p>
          </div>
        </div>
      )}

      {/* Signature Upload/Canvas Modal */}
      {showSignatureUploadModal && <SignatureUploadModal
        approverEmail={approver?.ApproverEmail}
        uploading={uploadingSignature}
        onSignatureSaved={handleSaveUserSignature}
        onCancel={() => setShowSignatureUploadModal(false)}
      />}

      {/* Reject Reason Modal */}
      {showRejectReason && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Reject CRAF</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              rows={5}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectReason(false);
                  setRejectReason("");
                }}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting || !rejectReason.trim()}
                className={`flex-1 px-4 py-2 text-sm rounded-lg font-medium ${
                  rejecting || !rejectReason.trim()
                    ? "bg-red-700/50 text-white cursor-not-allowed opacity-50"
                    : "bg-red-700 hover:bg-red-800 text-white cursor-pointer"
                }`}
              >
                {rejecting ? "Processing..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Signature Upload Modal Component — identical UX to the main contract's signing page
const SignatureUploadModal = ({ approverEmail, uploading, onSignatureSaved, onCancel }) => {
  const [uploadTab, setUploadTab] = React.useState("upload"); // "upload" or "canvas"
  const [uploadedImage, setUploadedImage] = React.useState(null);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSaveSignature = () => {
    if (uploadTab === "upload" && !uploadedImage) {
      alert("Please upload a signature image");
      return;
    }

    if (uploadTab === "canvas") {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const canvasData = canvas.toDataURL("image/png");
      if (canvasData === "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==") {
        alert("Please sign on the canvas before saving");
        return;
      }
      onSignatureSaved(canvasData);
    } else {
      onSignatureSaved(uploadedImage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-100">Your Signature</h2>
            <p className="text-sm text-slate-400 mt-1">Upload or sign on canvas to proceed</p>
          </div>
          <button
            onClick={onCancel}
            disabled={uploading}
            className="text-slate-400 hover:text-slate-100 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Info message */}
          <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-300">
              📝 <strong>Only you can use this signature.</strong> Once saved, your signature will be automatically used for all future approvals and will be visible to admins.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-700">
            <button
              onClick={() => setUploadTab("upload")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadTab === "upload"
                  ? "text-yellow-600 border-yellow-600"
                  : "text-slate-400 border-transparent hover:text-slate-300"
              }`}
            >
              📤 Upload Image
            </button>
            <button
              onClick={() => setUploadTab("canvas")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadTab === "canvas"
                  ? "text-yellow-600 border-yellow-600"
                  : "text-slate-400 border-transparent hover:text-slate-300"
              }`}
            >
              ✏️ Sign on Canvas
            </button>
          </div>

          {/* Upload Tab */}
          {uploadTab === "upload" && (
            <div>
              {uploadedImage ? (
                <div className="flex flex-col gap-4">
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 bg-slate-700/30">
                    <img src={uploadedImage} alt="Signature preview" className="max-h-32 mx-auto" />
                  </div>
                  <label className="block">
                    <span className="text-sm text-slate-300 mb-2 block">Change image:</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-yellow-600 file:text-slate-900"
                    />
                  </label>
                </div>
              ) : (
                <label className="block">
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-600/50 transition-colors">
                    <div className="text-4xl mb-2">📸</div>
                    <p className="text-slate-300 mb-1">Click to upload your signature</p>
                    <p className="text-xs text-slate-500">PNG, JPG or GIF (recommended: transparent background)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Canvas Tab */}
          {uploadTab === "canvas" && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-slate-400">Sign below. Touch or click and drag to draw your signature.</p>
              <canvas
                ref={canvasRef}
                width={500}
                height={150}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="border-2 border-slate-600 rounded-lg bg-white cursor-crosshair w-full touch-none"
                style={{ touchAction: "none" }}
              />
              <button
                onClick={clearCanvas}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg font-medium"
              >
                Clear
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onCancel}
              disabled={uploading}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSignature}
              disabled={uploading || (!uploadedImage && uploadTab === "upload")}
              className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                uploading || (!uploadedImage && uploadTab === "upload")
                  ? "bg-yellow-600/50 text-slate-900 cursor-not-allowed opacity-50"
                  : "bg-yellow-600 hover:bg-yellow-700 text-slate-900 cursor-pointer"
              }`}
            >
              {uploading ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
